import toNumber from 'lodash/toNumber';
import { stripNonNumericCharacters } from '@thesavingsgroup/utils/stripNonNumericCharacters';
import type { GetRatesForBrandsParams } from './types/GetRatesForBrandsParams';
import type { GetRatesForBrandsResults } from './types/GetRatesForBrandsResults';
import type { GetRateTableFunction } from './types/GetRateTableFunction';
import { Logger } from '@thesavingsgroup/logger/Logger';
import { getRateForDisplay } from './getRateForDisplay';
import { getRangeForDisplay } from './getRangeForDisplay';
import type { RateForDisplay } from './types/RateForDisplay';
import type { TSGBrand } from '@thesavingsgroup/remote-configuration/types/TSGBrand';
import type { CreditScoreRange } from './types/CreditScoreRange';
import type { LoanTerm } from './types/LoanTerm';

const LOWER_APR_CREDIT_SCORE: CreditScoreRange = '800+';
const UPPER_APR_CREDIT_SCORE: CreditScoreRange = '579 or lower';

const LOWER_APR_LOAN_TERM: LoanTerm = '24';
const UPPER_APR_LOAN_TERM: LoanTerm = '24';

const LOWER_LOAN_AMOUNT = 5_000;
const UPPER_LOAN_AMOUNT = 150_000;

export const getRatesForBrands =
  (getRateTable: GetRateTableFunction, logger = new Logger()) =>
  async ({
    creditScoreRange,
    downPayment,
    loanAmount,
    loanTerm,
    loanType,
  }: GetRatesForBrandsParams): Promise<GetRatesForBrandsResults> => {
    const rateTable = await getRateTable();

    const loanAmountAsNumber = toNumber(stripNonNumericCharacters(loanAmount));

    const brandEntries = Object.entries(
      // we want to favor the matching loan type, but it may not exist
      // refinance will always exist, so we can default to that
      rateTable[loanType] || rateTable.refinance!,
    );

    const rateForBrands = Object.fromEntries(
      brandEntries.map(([brand, brandRates]) => [
        brand,
        // If the loan amount is greater than 0, retrieve the specific rate for display
        // Otherwise, return a range-based rate display using predefined loan amounts
        loanAmountAsNumber > 0
          ? getRateForDisplay({
              apr: brandRates[creditScoreRange][loanTerm],
              brand,
              downPayment,
              loanAmount,
              loanTerm,
            })
          : // Use predefined values to determine the rate range for display.
            // These are values we determined to be proper based on the production rate table.
            getRangeForDisplay({
              brand,
              lowerApr: brandRates[LOWER_APR_CREDIT_SCORE][LOWER_APR_LOAN_TERM],
              lowerLoanAmount: LOWER_LOAN_AMOUNT,
              upperApr: brandRates[UPPER_APR_CREDIT_SCORE][UPPER_APR_LOAN_TERM],
              upperLoanAmount: UPPER_LOAN_AMOUNT,
            }),
      ]),
    ) as Record<TSGBrand, RateForDisplay>;

    logger.info('[RATE_TABLE]', {
      autopay: rateForBrands.autopay,
      creditScoreRange,
      downPayment,
      loanAmount,
      loanTerm,
      loanType,
      rateForBrands,
      rateGenius: rateForBrands.rateGenius,
      rateTable,
      tresl: rateForBrands.tresl,
    });

    return Object.assign(rateForBrands, { version: rateTable.version });
  };
